// ==================== APP COMPONENT ====================

import { lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Contexts
import { AuthProvider } from './contexts';

// Auth Components
import { ProtectedRoute, PublicOnlyRoute } from './components/auth';

// Layouts
import { PublicLayout, AuthLayout, DashboardLayout } from './layouts';

// Common Components
import { ScrollToTop } from './components/common';

// Public Pages
import { HomePage, AboutPage, ContactPage, FeaturesPage, PricingPage, PrivacyPage, TermsPage, WhitepapersPage } from './pages/public';

// Auth Pages
import { LoginPage, RegisterPage, VerifyEmailPage, ForgotPasswordPage } from './pages/auth';

// Dashboard Pages
import { AdminDashboard, CustomerDashboard } from './pages/dashboard';

// Admin Pages
import { CustomersPage, SubscriptionPlansPage, SubscriptionsPage, AdminSettingsPage, WhitepaperLeadsPage } from './pages/admin';

// Customer Pages
import { 
  PendingApprovalPage, 
  SelectPlanPage, 
  UpgradePlanPage,
  CheckoutPage, 
  PaymentSuccessPage, 
  PaymentCancelPage,
  SettingsLayout,
  ProfilePage,
  OrganizationPage,
  BillingPage,
  PaymentMethodsPage,
  PaymentHistoryPage,
  WhatsAppSettingsPage,
  SmsSettingsPage,
  CustomFieldsPage,
  GroupsPage,
  ContactsPage,
  TemplatesPage,
  SmsTemplatesPage,
  MessagesPage,
  KeywordBotsPage,
  CampaignsPage,
  CampaignAnalyticsPage,
} from './pages/customer';
import LeadGenPageWrapper from './pages/customer/lead-generation/LeadGenPageWrapper';

// Lead Generation pages are code-split (lazy) so their heavy deps (drag-and-drop
// Kanban, charts) load only when the customer opens Lead Generation. The Suspense
// boundary lives inside LeadGenPageWrapper so the page header renders instantly
// while the content chunk streams in.
const LeadGenDashboard = lazy(() => import('./pages/customer/lead-generation/LeadGenDashboard'));
const LeadDiscoveryPage = lazy(() => import('./pages/customer/lead-generation/LeadDiscoveryPage'));
const LeadDiscoveryCampaignPage = lazy(() => import('./pages/customer/lead-generation/LeadDiscoveryCampaignPage'));
const NewCampaignPage = lazy(() => import('./pages/customer/lead-generation/NewCampaignPage'));
const LeadPipelinePage = lazy(() => import('./pages/customer/lead-generation/LeadPipelinePage'));
const OutreachCampaignsPage = lazy(() => import('./pages/customer/lead-generation/OutreachCampaignsPage'));
const MeetingsBookedPage = lazy(() => import('./pages/customer/lead-generation/MeetingsBookedPage'));
const PerformanceReportsPage = lazy(() => import('./pages/customer/lead-generation/PerformanceReportsPage'));
const AgentSettingsPage = lazy(() => import('./pages/customer/lead-generation/AgentSettingsPage'));

// SMS Pages
import { SmsMessagesPage } from './pages/customer/sms';

// Constants
import { ROUTES } from './constants';

// Placeholder pages for routes that need to be implemented
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <h1 className="text-2xl font-heading font-bold text-white mb-2">{title}</h1>
      <p className="text-(--color-muted-gray)">This page is coming soon.</p>
    </div>
  </div>
);

// App wrapper component that provides auth context
const AppRoutes = () => {
  return (
    <Routes>
      {/* ==================== PUBLIC ROUTES ==================== */}
      <Route element={<PublicLayout />}>
        <Route path={ROUTES.HOME} element={<HomePage />} />
        <Route path={ROUTES.FEATURES} element={<FeaturesPage />} />
        <Route path={ROUTES.PRICING} element={<PricingPage />} />
        <Route path={ROUTES.ABOUT} element={<AboutPage />} />
        <Route path={ROUTES.CONTACT} element={<ContactPage />} />
        <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
        <Route path={ROUTES.TERMS} element={<TermsPage />} />
        <Route path={ROUTES.WHITEPAPERS} element={<WhitepapersPage />} />
      </Route>

      {/* ==================== AUTH ROUTES ==================== */}
      <Route element={<AuthLayout />}>
        <Route 
          path={ROUTES.AUTH.LOGIN} 
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          } 
        />
        <Route 
          path={ROUTES.AUTH.REGISTER} 
          element={
            <PublicOnlyRoute>
              <RegisterPage />
            </PublicOnlyRoute>
          } 
        />
        <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        {/* Reset password is handled within ForgotPasswordPage as a multi-step flow */}
        <Route path={ROUTES.AUTH.VERIFY_EMAIL} element={<VerifyEmailPage />} />
      </Route>

      {/* ==================== CUSTOMER SPECIAL ROUTES ==================== */}
      {/* Pending Approval - shown when customer is not yet approved */}
      <Route 
        path={ROUTES.CUSTOMER.ROOT + '/pending'} 
        element={
          <ProtectedRoute allowedRoles={['CUSTOMER']} requireActiveAccount={false}>
            <PendingApprovalPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Select Plan - shown when customer has no active subscription */}
      <Route 
        path={ROUTES.CUSTOMER.ROOT + '/select-plan'} 
        element={
          <ProtectedRoute allowedRoles={['CUSTOMER']} requireActiveAccount={true} requireSubscription={false}>
            <SelectPlanPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Payment Checkout - Stripe embedded checkout */}
      <Route 
        path={ROUTES.CUSTOMER.ROOT + '/payment/checkout'} 
        element={
          <ProtectedRoute allowedRoles={['CUSTOMER']} requireActiveAccount={true} requireSubscription={false}>
            <CheckoutPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Payment Success - shown after successful payment */}
      <Route 
        path={ROUTES.CUSTOMER.ROOT + '/payment/success'} 
        element={
          <ProtectedRoute allowedRoles={['CUSTOMER']} requireActiveAccount={true} requireSubscription={false}>
            <PaymentSuccessPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Payment Cancel - shown when payment is cancelled */}
      <Route 
        path={ROUTES.CUSTOMER.ROOT + '/payment/cancel'} 
        element={
          <ProtectedRoute allowedRoles={['CUSTOMER']} requireActiveAccount={true} requireSubscription={false}>
            <PaymentCancelPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Upgrade Plan - shown when customer wants to upgrade their plan */}
      <Route 
        path={ROUTES.CUSTOMER.UPGRADE_PLAN} 
        element={
          <ProtectedRoute allowedRoles={['CUSTOMER']} requireActiveAccount={true} requireSubscription={true}>
            <UpgradePlanPage />
          </ProtectedRoute>
        } 
      />

      {/* ==================== ADMIN DASHBOARD ROUTES ==================== */}
      <Route 
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <DashboardLayout role="SUPER_ADMIN" />
          </ProtectedRoute>
        }
      >
        <Route path={ROUTES.ADMIN.ROOT} element={<Navigate to={ROUTES.ADMIN.DASHBOARD} replace />} />
        <Route path={ROUTES.ADMIN.DASHBOARD} element={<AdminDashboard />} />
        <Route path={ROUTES.ADMIN.CUSTOMERS} element={<CustomersPage />} />
        <Route path={ROUTES.ADMIN.CUSTOMER_DETAIL} element={<PlaceholderPage title="Customer Details" />} />
        <Route path={ROUTES.ADMIN.SUBSCRIPTION_PLANS} element={<SubscriptionPlansPage />} />
        <Route path={ROUTES.ADMIN.SUBSCRIPTIONS} element={<SubscriptionsPage />} />
        <Route path={ROUTES.ADMIN.WHITEPAPER_LEADS} element={<WhitepaperLeadsPage />} />
        <Route path={ROUTES.ADMIN.SETTINGS} element={<AdminSettingsPage />} />
      </Route>

      {/* ==================== CUSTOMER DASHBOARD ROUTES ==================== */}
      <Route 
        element={
          <ProtectedRoute allowedRoles={['CUSTOMER']} requireActiveAccount={true} requireSubscription={true}>
            <DashboardLayout role="CUSTOMER" />
          </ProtectedRoute>
        }
      >
        <Route path={ROUTES.CUSTOMER.ROOT} element={<Navigate to={ROUTES.CUSTOMER.DASHBOARD} replace />} />
        <Route path={ROUTES.CUSTOMER.DASHBOARD} element={<CustomerDashboard />} />
        <Route path={ROUTES.CUSTOMER.ORGANIZATION} element={<PlaceholderPage title="Organization" />} />
        <Route path={ROUTES.CUSTOMER.WHATSAPP} element={<PlaceholderPage title="WhatsApp Integration" />} />
        <Route path={ROUTES.CUSTOMER.CONTACTS} element={<ContactsPage />} />
        <Route path={ROUTES.CUSTOMER.GROUPS} element={<GroupsPage />} />
        <Route path={ROUTES.CUSTOMER.CAMPAIGNS} element={<CampaignsPage />} />
        <Route path={ROUTES.CUSTOMER.CAMPAIGN_DETAIL} element={<CampaignsPage />} />
        <Route path={ROUTES.CUSTOMER.CAMPAIGN_ANALYTICS} element={<CampaignAnalyticsPage />} />
        <Route path={ROUTES.CUSTOMER.TEMPLATES} element={<TemplatesPage />} />
        <Route path={ROUTES.CUSTOMER.SMS_TEMPLATES} element={<SmsTemplatesPage />} />
        <Route path={ROUTES.CUSTOMER.MESSAGES} element={<MessagesPage />} />
        <Route path={ROUTES.CUSTOMER.KEYWORD_BOTS} element={<KeywordBotsPage />} />
        <Route path={ROUTES.CUSTOMER.CHAT} element={<MessagesPage />} />
        <Route path={ROUTES.CUSTOMER.SMS_MESSAGES} element={<SmsMessagesPage />} />
        {/* Analytics route - placeholder for future implementation */}
        <Route path={ROUTES.CUSTOMER.BILLING} element={<Navigate to={ROUTES.CUSTOMER.SETTINGS_BILLING} replace />} />
        
        {/* Lead Generation Routes */}
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_DASHBOARD} element={<LeadGenPageWrapper><LeadGenDashboard /></LeadGenPageWrapper>} />
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_DISCOVERY} element={<LeadGenPageWrapper><LeadDiscoveryPage /></LeadGenPageWrapper>} />
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_NEW_CAMPAIGN} element={<LeadGenPageWrapper><NewCampaignPage /></LeadGenPageWrapper>} />
        <Route path={`${ROUTES.CUSTOMER.LEAD_GEN_DISCOVERY}/:runId`} element={<LeadGenPageWrapper><LeadDiscoveryCampaignPage /></LeadGenPageWrapper>} />
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_PIPELINE} element={<LeadGenPageWrapper><LeadPipelinePage /></LeadGenPageWrapper>} />
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_OUTREACH} element={<LeadGenPageWrapper><OutreachCampaignsPage /></LeadGenPageWrapper>} />
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_MEETINGS} element={<LeadGenPageWrapper><MeetingsBookedPage /></LeadGenPageWrapper>} />
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_REPORTS} element={<LeadGenPageWrapper><PerformanceReportsPage /></LeadGenPageWrapper>} />
        <Route path={ROUTES.CUSTOMER.LEAD_GEN_SETTINGS} element={<LeadGenPageWrapper><AgentSettingsPage /></LeadGenPageWrapper>} />
        
        {/* Settings Routes with Nested Layout */}
        <Route path={ROUTES.CUSTOMER.SETTINGS} element={<SettingsLayout />}>
          <Route index element={<Navigate to={ROUTES.CUSTOMER.SETTINGS_PROFILE} replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="organization" element={<OrganizationPage />} />
          <Route path="whatsapp" element={<WhatsAppSettingsPage />} />
          <Route path="sms" element={<SmsSettingsPage />} />
          <Route path="custom-fields" element={<CustomFieldsPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="payment-methods" element={<PaymentMethodsPage />} />
          <Route path="payments" element={<PaymentHistoryPage />} />
        </Route>
      </Route>

      {/* ==================== ERROR ROUTES ==================== */}
      <Route path={ROUTES.NOT_FOUND} element={<PlaceholderPage title="404 - Page Not Found" />} />
      <Route path={ROUTES.UNAUTHORIZED} element={<PlaceholderPage title="401 - Unauthorized" />} />
      <Route path={ROUTES.FORBIDDEN} element={<PlaceholderPage title="403 - Forbidden" />} />
      <Route path={ROUTES.SERVER_ERROR} element={<PlaceholderPage title="500 - Server Error" />} />

      {/* Catch all - redirect to 404 */}
      <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
